import React from 'react';
import { Link } from 'react-router';
import SEO from '../components/SEO';
import GlowCard from '../components/GlowCard';
import { trackEvent } from '../components/analytics';

const features = [
  {
    title: 'Core SMS Records',
    description:
      'Hazards, occurrences, change requests, audits, and CAPA tracked in status workflows with evidence.',
  },
  {
    title: 'Risk Assessment Engine',
    description:
      'Configurable risk matrix with multi-hazard scoring and risk-level classification for consistent decisions.',
  },
  {
    title: 'Mitigation Planning',
    description:
      'Action tracking with due dates and CAPA linkage to close hazard controls.',
  },
  {
    title: 'CAPA Management',
    description:
      'Action ownership, verification, and effectiveness reviews keep corrective actions accountable.',
  },
  {
    title: 'Governance and Accountability',
    description:
      'Accountable Executive approvals, accountability map, and review cadence for safety governance.',
  },
  {
    title: 'Audit Program and Findings',
    description:
      'Audit schedules, finding severity, root cause analysis, and closure criteria in one trail.',
  },
  {
    title: 'Safety Performance (SPIs)',
    description:
      'SPI governance with lineage, evaluations, threshold changes, and breach alerts.',
  },
  {
    title: 'Dashboards and Evidence',
    description:
      'KPI dashboard, snapshots, PDF exports, and audit-ready outputs.',
  },
  {
    title: 'Reportability and SLA Tracking',
    description:
      'ICAO, EASA, FAA, and EU 376 reportability logic with 72-hour deadline tracking.',
  },
  {
    title: 'Compliance Packs and Exports',
    description:
      'Evidence bundles and regulator submissions packaged as exportable compliance packs.',
  },
  {
    title: 'Safety Promotion',
    description:
      'Bulletins, campaigns, lessons learned, and acknowledgement tracking.',
  },
  {
    title: 'Training and Competency',
    description:
      'Training triggers, requests, effectiveness reviews, and training evidence bundles.',
  },
  {
    title: 'FRMS',
    description:
      'Fatigue reports aligned to duty and rest policies with schedule compliance and variance tracking.',
  },
  {
    title: 'Monitoring and Intelligence',
    description:
      'External alerts, flight data events, and maintenance signals for proactive monitoring.',
  },
  {
    title: 'Specialized Programs',
    description:
      'Aerodrome safety, ERP plans and drills, and separation safety cases.',
  },
  {
    title: 'Regulator Interface',
    description:
      'Regulator integrations, code lists, SSP overview, audits, and checklists.',
  },
  {
    title: 'Access and Security',
    description:
      'Access profiles, access logs, reporter protection, and just culture controls.',
  },
  {
    title: 'SMS Public Portal',
    description:
      'Branded domain for external users with assigned document access and OTP login.',
  },
  {
    title: 'Mobile App (Planned)',
    description:
      'Offline reporting, evidence capture, secure sync, and push alerts.',
  },
  {
    title: 'Compliance Coverage',
    description:
      'ICAO Annex 19 and 13, EASA Part-ORO, FAA Part 830, EU 376/2014, GACAR, and UK CAA coverage with regime tagging.',
  },
];

const SmsFeaturesPage: React.FC = () => {
  return (
    <div className="animate-fade-in-up">
      <SEO
        title="SMS Feature Coverage"
        description="Regulator-ready SMS feature coverage across web SMS workflows, public portal access, and a planned mobile app."
      />

      <section className="py-24 bg-slate-950 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <nav className="flex items-center space-x-2 text-slate-500 text-sm mb-6 uppercase tracking-widest">
              <span>Modules</span>
              <span>/</span>
              <span className="text-success">SMS</span>
              <span>/</span>
              <span>Features</span>
            </nav>
            <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-6 leading-tight">
              SMS Feature Coverage
              <span className="block text-success">Built for Regulator-Ready Operations.</span>
            </h1>
            <p className="text-xl text-slate-400 mb-6 leading-relaxed">
              Web SMS workflows for safety teams, a controlled SMS public portal, and a planned mobile app for field reporting.
            </p>
            <p className="text-xs uppercase tracking-widest text-slate-500">
              Evidence-first governance aligned to ICAO, EASA, FAA, and EU 376/2014 oversight.
            </p>
          </div>
          <div className="bg-white/5 border border-white/10 p-8 rounded-2xl">
            <p className="text-slate-300 text-base leading-relaxed">
              Every feature is designed to move safety data from report to regulator-ready evidence with accountable approvals,
              traceability, and defensible timelines.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-4 text-white">SMS Features by Capability</h2>
            <p className="text-slate-400 text-base leading-relaxed max-w-3xl">
              One card per capability, covering web SMS operations, public portal access, and planned mobile workflows.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {features.map((feature) => (
              <GlowCard key={feature.title} className="h-full">
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
              </GlowCard>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <Link
            to="/contact"
            className="inline-flex px-10 py-4 bg-accent text-primary font-bold rounded-xl"
            onClick={() => trackEvent('demo_request', { form_id: 'sms_features_cta_walkthrough', module: 'sms' })}
          >
            Request an SMS Feature Walkthrough
          </Link>
          <p className="text-slate-400 text-sm mt-4">
            Focused on audit readiness, reporting timelines, and operational oversight.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SmsFeaturesPage;
